// copyToClipboard writes the given value to the system clipboard, falling
// back to a hidden <textarea> + execCommand for browsers that don't expose
// navigator.clipboard (or where it rejects, e.g. over insecure contexts).
//
// It returns a promise resolving to { ok, error? } so callers can show a
// graceful failure message instead of throwing.
export type CopyResult = { ok: true } | { ok: false; error: string };

export async function copyToClipboard(value: string): Promise<CopyResult> {
  const clipboard =
    typeof navigator !== "undefined" ? navigator.clipboard : undefined;
  if (clipboard && typeof clipboard.writeText === "function") {
    try {
      await clipboard.writeText(value);
      return { ok: true };
    } catch {
      return legacyCopy(value);
    }
  }
  return legacyCopy(value);
}

function legacyCopy(value: string): CopyResult {
  try {
    if (typeof document === "undefined") {
      return { ok: false, error: "Clipboard unavailable" };
    }
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    ta.style.pointerEvents = "none";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok
      ? { ok: true }
      : { ok: false, error: "Copy failed — select and copy manually" };
  } catch {
    return { ok: false, error: "Copy failed — select and copy manually" };
  }
}
